// =====================================================
// AI TRADE REVIEW DISPLAY COMPONENT
// Displays structured AI review with scores and sentiment
// =====================================================

import React from 'react';
import SafeIcon from '../SafeIcon';
import { FiTarget, FiShield, FiHeart, FiCheckCircle, FiAlertCircle, FiArrowRight } from 'react-icons/fi';

interface AIReviewData {
  summary: string;
  strengths: string[];
  mistakes: string[];
  recommendations: string[];
  risk_score: number;
  discipline_score: number;
  emotional_control_score: number;
  sentiment_label: string;
}

interface AITradeReviewDisplayProps {
  review: AIReviewData | null;
  isLoading?: boolean;
}

const getSentimentColor = (sentiment: string): string => {
  const colors: Record<string, string> = {
    'Calm': 'bg-green-500/20 text-green-400 border-green-500/30',
    'Rushed': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    'Greedy': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    'Fearful': 'bg-red-500/20 text-red-400 border-red-500/30',
    'Overconfident': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  };
  return colors[sentiment] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
};

const getScoreColor = (score: number): string => {
  if (score >= 8) return 'text-green-400';
  if (score >= 6) return 'text-yellow-400';
  if (score >= 4) return 'text-orange-400';
  return 'text-red-400';
};

const getScoreBarColor = (score: number): string => {
  if (score >= 8) return 'bg-gradient-to-r from-green-500 to-green-400';
  if (score >= 6) return 'bg-gradient-to-r from-yellow-500 to-yellow-400';
  if (score >= 4) return 'bg-gradient-to-r from-orange-500 to-orange-400';
  return 'bg-gradient-to-r from-red-500 to-red-400';
};

const ScoreCard: React.FC<{
  label: string;
  score: number;
  icon: React.ComponentType<{ className?: string }>;
}> = ({ label, score, icon }) => (
  <div className="p-3 rounded-lg bg-gray-900/60 border border-gray-700/50">
    <div className="flex items-center gap-2 mb-2">
      <SafeIcon icon={icon} className="w-4 h-4 text-gray-400" />
      <span className="text-xs text-gray-400">{label}</span>
    </div>
    <div className="flex items-center gap-3">
      <span className={`text-2xl font-bold ${getScoreColor(score)}`}>{score}</span>
      <span className="text-xs text-gray-500">/10</span>
    </div>
    <div className="mt-2 h-1.5 bg-gray-800 rounded-full overflow-hidden">
      <div 
        className={`h-full rounded-full transition-all ${getScoreBarColor(score)}`}
        style={{ width: `${score * 10}%` }}
      />
    </div>
  </div>
);

const BulletList: React.FC<{
  title: string;
  items: string[];
  icon: React.ComponentType<{ className?: string }>;
  iconColor: string;
}> = ({ title, items, icon, iconColor }) => (
  <div className="space-y-2">
    <h5 className="text-sm font-medium text-gray-300 flex items-center gap-2">
      <SafeIcon icon={icon} className={`w-4 h-4 ${iconColor}`} />
      {title}
    </h5>
    {items.length > 0 ? (
      <ul className="space-y-1.5">
        {items.map((item, idx) => (
          <li key={idx} className="text-sm text-gray-400 flex items-start gap-2">
            <span className="text-gray-600 mt-1">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    ) : (
      <p className="text-sm text-gray-500 italic">No items to display</p>
    )}
  </div>
);

const AITradeReviewDisplay: React.FC<AITradeReviewDisplayProps> = ({ review, isLoading }) => {
  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-4 bg-gray-700 rounded w-3/4" />
        <div className="h-4 bg-gray-700 rounded w-1/2" />
        <div className="grid grid-cols-3 gap-3">
          <div className="h-24 bg-gray-700 rounded" />
          <div className="h-24 bg-gray-700 rounded" />
          <div className="h-24 bg-gray-700 rounded" />
        </div>
      </div>
    );
  }

  if (!review) {
    return (
      <p className="text-gray-500 text-sm">
        No AI review yet. Click "Generate AI Review" to get feedback on this trade.
      </p>
    );
  }

  return (
    <div className="space-y-5">
      {/* Summary */}
      <div className="space-y-2">
        <p className="text-sm text-gray-300 leading-relaxed">{review.summary}</p>
        
        {/* Sentiment Badge */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Emotional State:</span>
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${getSentimentColor(review.sentiment_label)}`}>
            {review.sentiment_label}
          </span>
        </div>
      </div>

      {/* Score Cards */}
      <div className="grid grid-cols-3 gap-3">
        <ScoreCard 
          label="Risk Management" 
          score={review.risk_score} 
          icon={FiShield}
        />
        <ScoreCard 
          label="Discipline" 
          score={review.discipline_score} 
          icon={FiTarget}
        />
        <ScoreCard 
          label="Emotional Control" 
          score={review.emotional_control_score} 
          icon={FiHeart}
        />
      </div>

      {/* Strengths, Mistakes, Recommendations */}
      <div className="space-y-4 pt-2">
        <BulletList 
          title="Strengths" 
          items={review.strengths} 
          icon={FiCheckCircle}
          iconColor="text-green-400"
        />
        
        <BulletList 
          title="Mistakes" 
          items={review.mistakes} 
          icon={FiAlertCircle}
          iconColor="text-red-400"
        />
        
        <BulletList 
          title="Recommendations" 
          items={review.recommendations} 
          icon={FiArrowRight}
          iconColor="text-cyan-400"
        />
      </div>
    </div>
  );
};

export default AITradeReviewDisplay;
